import { FieldModel, UserId } from '@cibo/core'
import { FieldLabel, LandManagementContext, PaginatedFieldsContext } from '@cibo/landmanager'
import { AuthUserPermission, Can, Loading } from '@cibo/profile'
import { FieldStatusChips, ProgramOpportunitiesDisplay } from '@cibo/programs'
import { LandThumbnail, LandThumbnailSkin, intuitiveFromNow, useFigures } from '@cibo/ui'
import AddchartRounded from '@mui/icons-material/AddchartRounded'
import DeleteRounded from '@mui/icons-material/DeleteRounded'
import EditRounded from '@mui/icons-material/EditRounded'
import GestureRounded from '@mui/icons-material/GestureRounded'
import PersonAddRounded from '@mui/icons-material/PersonAddRounded'
import PlaylistAddCheckRounded from '@mui/icons-material/PlaylistAddCheckRounded'
import { Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { Box } from '@mui/system'
import ROUTE, {
  ENROLLMENT_ROUTES,
  LAND_MANAGER_ROUTES,
  PROGRAM_ROUTES,
} from 'navigation/types/routes'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Popup } from 'react-map-gl/dist/es5'
import { generatePath, useNavigate } from 'react-router-dom'
import { ENGAGEMENT_PATHS } from '../../../navigation/types/engagementRoutes'

interface FieldPopoverProps {
  field?: FieldModel
  fieldIsFetched: boolean
  fieldIsLoading: boolean
  onClickMeta(resourceId: string): void
  onClickOpportunity(opportunity: { programId: string; field: FieldModel }): void
  onClose(): void
  showPopup: { longitude: number; latitude: number }
}

export const FieldPopover = ({
  field,
  fieldIsFetched,
  fieldIsLoading,
  onClickMeta,
  onClickOpportunity,
  onClose,
  showPopup,
}: FieldPopoverProps) => {
  const { t } = useTranslation('impact/pages/LandManagerMap')
  const { onDelete } = useContext(PaginatedFieldsContext)
  const { acres } = useFigures()
  const { setAssignFieldsModal } = useContext(LandManagementContext)
  const navigate = useNavigate()

  const onClickNeedsInfo = (field: FieldModel) =>
    navigate(generatePath(ROUTE.PROFILE_FIELD, { fieldId: field.resourceId }))

  return (
    <Popup
      longitude={showPopup.longitude}
      latitude={showPopup.latitude}
      offset={[0, -12]}
      onClose={onClose}
      maxWidth="350"
    >
      <Box sx={{ pointerEvents: 'all' }}>
        {fieldIsLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 350,
              height: 400,
            }}
          >
            <Loading />
          </Box>
        )}
        {field && (
          <Stack spacing={2} width={350}>
            <Stack spacing={2} direction="row">
              <Box borderRadius={1} overflow="hidden">
                <LandThumbnail
                  landProfile={field}
                  width={60}
                  height={60}
                  skin={LandThumbnailSkin.SMALL}
                />
              </Box>
              <Stack>
                <Typography variant="body2">{field.name}</Typography>
                <Typography variant="caption">{field.locationLabel}</Typography>
                <Stack direction="row" gap={1} rowGap={0.5} flexWrap="wrap">
                  {field.labels.map(label => (
                    <FieldLabel key={label} label={label} />
                  ))}
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography>{acres(field.acres, false)}</Typography>
              <FieldStatusChips
                field={field}
                outerStackProps={{ alignItems: 'flex-end' }}
                onClickNeedsInfo={onClickNeedsInfo}
                onClickEnrolling={({ workflowId }: { workflowId: string }) =>
                  navigate(generatePath(ENROLLMENT_ROUTES.DETAIL, { workflowId }))
                }
                onClickEngaged={({
                  engagementId,
                  programId,
                  userId,
                }: {
                  engagementId: string
                  programId: string
                  userId: UserId
                }) => {
                  navigate(
                    generatePath(`${PROGRAM_ROUTES.ENGAGEMENTS}/${ENGAGEMENT_PATHS.ENGAGEMENT}`, {
                      engagementId,
                      programId,
                    })
                  )
                }}
                onClickProgram={(programId: string) =>
                  navigate(generatePath(PROGRAM_ROUTES.DETAIL, { programId }))
                }
              />
            </Stack>
            <Can useAny={AuthUserPermission.GROWER_CONTACT_READ}>
              <Stack>
                <Typography variant="caption">{t('owner')}</Typography>
                <Typography sx={{ wordBreak: 'break-all' }}>{field.ownerLabel}</Typography>
              </Stack>
            </Can>
            <ProgramOpportunitiesDisplay
              isFetched={fieldIsFetched}
              display="grid"
              field={field}
              onClickNeedsInfo={e => {
                onClickNeedsInfo(e)
              }}
              onClickOpportunity={({ programId, field }) => {
                onClickOpportunity({ programId, field })
              }}
            >
              <Typography variant="overline">{t('moreOpportunities')}</Typography>
            </ProgramOpportunitiesDisplay>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              {!!field.updatedAt && (
                <Stack>
                  <Typography variant="caption">{t('lastUpdated')}</Typography>
                  <Typography variant="caption">{intuitiveFromNow(field.updatedAt)}</Typography>
                </Stack>
              )}
              {!!field.updatedAt && (
                <Stack>
                  <Typography variant="caption" textAlign="right">
                    {t('createdAt')}
                  </Typography>
                  <Typography variant="caption" textAlign="right">
                    {intuitiveFromNow(field.updatedAt)}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={1}>
                <Tooltip title={t('editBoundaries')}>
                  <IconButton
                    onClick={() =>
                      navigate(
                        generatePath(LAND_MANAGER_ROUTES.DETAIL_EDIT_BOUNDARY, {
                          resourceId: field.id as string,
                        })
                      )
                    }
                    disabled={!field.can(['edit_land'])}
                  >
                    <GestureRounded />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('editNameAndLabel')}>
                  <IconButton
                    onClick={() => onClickMeta(field.resourceId)}
                    disabled={!field.can(['edit_meta'])}
                  >
                    <EditRounded />
                  </IconButton>
                </Tooltip>
                {!!field.ownerId ? (
                  <Can useAny={AuthUserPermission.FIELDS_OWNER_MODIFY}>
                    <Tooltip title={t('grower')}>
                      <IconButton
                        onClick={() =>
                          setAssignFieldsModal({
                            open: true,
                            selectedIds: [field.id as string],
                          })
                        }
                        disabled={!field.can(['field_assign'])}
                      >
                        <PersonAddRounded />
                      </IconButton>
                    </Tooltip>
                  </Can>
                ) : (
                  <Can useAny={AuthUserPermission.FIELDS_OWNER_ASSIGN}>
                    <Tooltip title={t('grower')}>
                      <IconButton
                        onClick={() =>
                          setAssignFieldsModal({
                            open: true,
                            selectedIds: [field.id as string],
                          })
                        }
                        disabled={!field.can(['field_assign'])}
                      >
                        <PersonAddRounded />
                      </IconButton>
                    </Tooltip>
                  </Can>
                )}
                <Tooltip
                  title={t('information', {
                    context: field.isMissingProfile ? 'incomplete' : undefined,
                  })}
                >
                  <IconButton onClick={() => onClickNeedsInfo(field)}>
                    {field.isMissingProfile ? <AddchartRounded /> : <PlaylistAddCheckRounded />}
                  </IconButton>
                </Tooltip>
              </Stack>
              <Tooltip title={t('delete')}>
                <IconButton
                  disabled={!field.can(['delete'])}
                  onClick={() => onDelete(field.resourceId)}
                >
                  <DeleteRounded />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        )}
      </Box>
    </Popup>
  )
}
